import { money, pct } from './format.js';
import { getExtractionVerification, getAccountReconciliation } from '../../store/assessmentSelectors.js';
import AccountReconciliation from './AccountReconciliation.jsx';

// The planner's working copy of what came out of the statements. Extraction
// from a PDF is the step most likely to be wrong, and the planner holding the
// statement is the only person who can catch it, so when the two reads disagree
// this opens by itself instead of waiting to be clicked. Never printed, never
// shown in view mode: this is a tool, not part of the deliverable.

// foundInPasses arrives as either a count or the list of passes it appeared in.
const passCount = v => {
  if (Array.isArray(v)) return v.length;
  return Number.isFinite(v) ? v : null;
};

function DiscrepancyTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="asmt-review-block">
      <div className="asmt-review-subhead">Found in one read and not the other</div>
      <table className="asmt-table">
        <thead>
          <tr>
            <th>Holding</th>
            <th>Account</th>
            <th className="num">Value</th>
            <th className="num">Reads</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => {
            const seen = passCount(d.foundInPasses);
            // No row highlight: the whole block is already the warning, and
            // amber on amber only flattens it.
            return (
              <tr key={`${d.ticker || d.name}-${i}`}>
                <td>
                  <strong>{d.name || d.ticker || 'Position'}</strong>
                  {d.name && d.ticker ? <span className="asmt-muted"> · {d.ticker}</span> : null}
                </td>
                <td>{d.account || <span className="asmt-muted">not stated</span>}</td>
                <td className="num">{money(Number(d.value))}</td>
                <td className="num">{seen === null ? '-' : `${seen} of 2`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DuplicateTable({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="asmt-review-block">
      <div className="asmt-review-subhead">Read twice in one pass, collapsed to one</div>
      <table className="asmt-table">
        <thead>
          <tr>
            <th>Holding</th>
            <th>Account</th>
            <th className="num">Kept</th>
            <th className="num">Discarded</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => (
            <tr key={`${d.ticker}-${i}`}>
              <td><strong>{d.ticker || 'Position'}</strong></td>
              <td>{d.account || <span className="asmt-muted">not stated</span>}</td>
              <td className="num">{money(Number(d.keptValue))}</td>
              <td className="num asmt-muted">{money(Number(d.discardedValue))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="asmt-review-note">
        The same position came back twice in one read. Confirm the kept figure is the one on the
        statement, and that the discarded figure is not a second real position.
      </p>
    </div>
  );
}

function PositionsTable({ holdings, unpricedRows }) {
  return (
    <table className="asmt-table">
      <thead>
        <tr>
          <th>Ticker</th>
          <th>Name</th>
          <th>Account</th>
          <th className="num">Value</th>
          <th className="num">Weight</th>
          <th>Pool</th>
        </tr>
      </thead>
      <tbody>
        {holdings.map((h, i) => (
          <tr key={`${h.ticker}-${i}`}>
            <td>{h.ticker || '-'}</td>
            <td>{h.name || '-'}</td>
            <td>{h.account || '-'}</td>
            <td className="num">{money(Number(h.value))}</td>
            <td className="num">{pct(Number(h.weightPct))}</td>
            <td>{h.pool || '-'}</td>
          </tr>
        ))}
        {unpricedRows.map((h, i) => (
          <tr key={`unpriced-${h.ticker || h.name}-${i}`}>
            <td>{h.ticker || '-'}</td>
            <td>{h.name || '-'}</td>
            <td>{h.account || '-'}</td>
            <td className="num">{money(Number(h.value))}</td>
            <td className="num asmt-muted">no price</td>
            <td>{h.pool || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ExtractionReview({ bundle }) {
  const holdings = Array.isArray(bundle?.holdings) ? bundle.holdings.filter(Boolean) : [];
  const unpricedRows = Array.isArray(bundle?.unpricedHoldings)
    ? bundle.unpricedHoldings.filter(Boolean)
    : [];
  if (!holdings.length && !unpricedRows.length) return null;

  const verification = getExtractionVerification(bundle);
  const accounts = getAccountReconciliation(bundle);
  const needsReview = Boolean(verification?.needsReview);
  const positions = holdings.length + unpricedRows.length;
  // Falls back to the parts rather than showing $0, which would read as a
  // finding rather than as a missing key.
  const stated = Number(bundle?.portfolio?.totalValue)
    || [...holdings, ...unpricedRows].reduce((s, h) => s + (Number(h.value) || 0), 0);

  return (
    <div className="asmt-review no-print">
      {needsReview && (
        <div className="asmt-review-alert" role="status">
          <strong>The two reads of these statements did not agree.</strong>
          <p>
            Every statement is read twice and the two results compared. Anything below was found in
            one read and not in the other, so either it is real and one read missed it, or it is not
            real and one read invented it. Only the statement settles which. Check these lines
            against the statement before this assessment goes to the client.
          </p>
          <DiscrepancyTable rows={verification.discrepancies} />
          <DuplicateTable rows={verification.collapsedDuplicates} />
        </div>
      )}

      <details className="asmt-review-details" open={needsReview}>
        <summary>
          {verification && verification.doubleChecked && !needsReview && (
            <span className="asmt-review-ok">Two reads of these statements matched.</span>
          )}{' '}
          Check what was read from the statements ({positions} position{positions === 1 ? '' : 's'},{' '}
          {money(stated)})
        </summary>
        <AccountReconciliation accounts={accounts} />
        <div className="asmt-review-block">
          <div className="asmt-review-subhead">Every position read</div>
          <PositionsTable holdings={holdings} unpricedRows={unpricedRows} />
        </div>
      </details>
    </div>
  );
}
